<template>
  <div>
    <v-row no-gutters>
      <v-col>
        <v-textarea
          ref="textarea"
          v-model="textLocal"
          v-bind="$attrs"
          class="emoji-textarea"
          spellcheck="false"
          :counter="maxLengthCounter"
          :error="error"
          :error-messages="errorMessages"
        >
          <template v-if="showSticker" slot="append">
            <v-menu ref="menu" v-model="menu" transition="scale-transition" offset-y min-width="290px" :close-on-content-click="false" bottom eager>
              <template v-slot:activator="{ on, attrs }">
                <mf-button icon="mood" color="cinza-0" type="icon" size="sm" class="mt-0" @click="menu = true" />
              </template>
              <picker :data="emojiIndex" :i18n="i18n" native title="Emojis" emoji="shopping_trolley" emoji-tooltip @select="selectEmoji" />
            </v-menu>
          </template>
        </v-textarea>
        <v-row v-if="!hideCounter" class="input-details pl-4 mr-1" justify="space-between">
          <span class="mr-2 error--text"> {{ errorMessages }}</span>
          <span class="right-text" :class="charsUsed > maxLength ? 'error--text' : ''"
            >{{ avoidVariables ? textLocal.length : charsUsed }} / {{ maxLength }}</span
          >
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script>
// Import default CSS
import 'emoji-mart-vue-fast/css/emoji-mart.css'

import { Picker, EmojiIndex } from 'emoji-mart-vue-fast'
import { characterVariables } from '@/mixins/character-variables'
import data from 'emoji-mart-vue-fast/data/twitter.json'

const EMOJI_LENGTH_IN_CHAR = 2
const EXCLUDED_EMOJIS = [
  'smiling_face_with_tear',
  'disguised_face',
  'pinched_fingers',
  'anatomical_heart',
  'lungs',
  'red_haired_person',
  'curly_haired_person',
  'white_haired_person',
  'bald_person',
  'health_worker',
  'student',
  'judge',
  'teacher',
  'farmer',
  'cook',
  'factory_worker',
  'mechanic',
  'office_worker',
  'scientist',
  'technologist',
  'singer',
  'pilot',
  'artist',
  'astronaut',
  'firefighter',
  'ninja',
  'man_in_tuxedo',
  'woman_in_tuxedo',
  'man_with_veil',
  'woman_with_veil',
  'woman_feeding_baby',
  'man_feeding_baby',
  'person_feeding_baby',
  'mx_claus',
  'person_with_probing_cane',
  'person_in_manual_wheelchair',
  'person_in_motorized_wheelchair',
  'people_hugging',
  'black_cat',
  'bison',
  'mammoth',
  'beaver',
  'dodo',
  'feather',
  'seal',
  'beetle',
  'cockroach',
  'worm',
  'fly',
  'potted_plant',
  'blueberries',
  'olive',
  'bell_pepper',
  'flatbread',
  'tamale',
  'fondue',
  'teapot',
  'bubble_tea',
  'magic_wand',
  'pinata',
  'nesting_dolls',
  'sewing_needle',
  'knot',
  'rock',
  'wood',
  'hut',
  'pickup_truck',
  'roller_skate',
  'thong_sandal',
  'military_helmet',
  'accordion',
  'long_drum',
  'coin',
  'boomerang',
  'carpentry_saw',
  'screwdriver',
  'hook',
  'ladder',
  'elevator',
  'mirror',
  'window',
  'plunger',
  'mouse_trap',
  'bucket',
  'toothbrush',
  'headstone',
  'placard',
]
const EXLCUDED_CATEGORIES = ['flags']

const emojisToShowFilter = (emoji) => {
  return !EXCLUDED_EMOJIS.includes(emoji.short_names[0])
}

let emojiIndex = new EmojiIndex(data, {
  exclude: EXLCUDED_CATEGORIES,
  emojisToShowFilter,
})

export default {
  name: 'mf-textarea-emoji',
  components: {
    Picker,
  },
  mixins: [characterVariables],
  props: {
    value: String,
    errorMessages: {
      type: [String, Array],
      default: '',
    },
    showSticker: {
      type: Boolean,
      default: true,
    },
    maxLength: {
      type: Number,
      default: 160,
    },
    hideCounter: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    avoidVariables: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    menu: false,
    emojiIndex: emojiIndex,
  }),
  computed: {
    i18n() {
      return {
        search: 'Buscar',
        notfound: 'Nenhum Emoji Encontrado',
        categories: {
          search: 'Resultados da Busca',
          recent: 'Usados Frequentemente',
          people: 'Smileys & Pessoas',
          nature: 'Animais & Natureza',
          foods: 'Comidas & Bebidas',
          activity: 'Atividades',
          places: 'Viagens & Lugares',
          objects: 'Objetos',
          symbols: 'Simbolos',
          flags: 'Bandeiras',
          custom: 'Customizados',
        },
      }
    },
    maxLengthCounter() {
      return this.lengthCounter(this.textLocal, this.maxLength)
    },
  },
  watch: {
    value() {
      this.updateCharsUsedLength()
    },
  },
  mounted() {
    this.updateCharsUsedLength()
  },
  methods: {
    insertIntoCursorPosition(insertText) {
      const textarea = this.$refs.textarea.$refs.input
      const sentence = textarea.value
      const len = sentence.length
      let pos = textarea.selectionStart
      if (pos === undefined) {
        pos = 0
      }
      const before = sentence.substr(0, pos)
      const after = sentence.substr(pos, len) || ''

      this.textLocal = before + insertText + after
      this.$nextTick().then(() => {
        textarea.selectionStart = pos + insertText.length
        textarea.selectionEnd = pos + insertText.length
        textarea.focus()
      })
    },
    selectEmoji(emoji) {
      if (this.maxLength) {
        if (
          this.maxLength && this.avoidVariables
            ? this.textLocal.length + EMOJI_LENGTH_IN_CHAR > this.maxLength
            : this.charsUsed + EMOJI_LENGTH_IN_CHAR > this.maxLength
        ) {
          this.$snackbar({ message: 'Não é possível inserir emojis devido ao tamanho limite do campo.' })
          return
        }
      }
      this.insertIntoCursorPosition(emoji.native)
    },
  },
}
</script>

<style lang="scss">
.input-details {
  font-size: 12px;
  padding-left: 12px 4px;
  color: rgba(0, 0, 0, 0.54);
}
.emoji-textarea textarea {
  margin-bottom: 10px;
  margin-right: 3px;
}
</style>
